import React, { useEffect, useState, useCallback } from "react"
import { connect } from "react-redux"
import {
  Table,
  Button,
  Select,
  Popconfirm,
  Image,
  Space,
  Upload,
  Card,
  Typography,
  Tag,
  Empty,
  Spin,
  Row,
  Col,
  Progress,
  message,
  Tooltip,
  Badge,
  Modal,
} from "antd"
import {
  DeleteOutlined,
  UploadOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileZipOutlined,
  DownloadOutlined,
  EyeOutlined,
  InboxOutlined,
  CloudUploadOutlined,
  PaperClipOutlined,
} from "@ant-design/icons"
import { withTranslation } from "react-i18next"

import {
  getDocuments,
  addDocument,
  deleteDocument,
  resetDocumentFlag,
  clearDocuments,
} from "store/actions"
import * as url from "helpers/url_helper"

import { getApiUrlNotificationFiles } from "common/global"
import { documentTypeOptions } from "models/types/document-type-enum"

const { Text, Title } = Typography
const { Dragger } = Upload

// API URL from environment
const API_URL = process.env.REACT_APP_API_URL

// Entity Types Enum
export const EntityType = {
  Notification: 1,
  PackageInstaller: 6,
  // Add more entity types as needed
}

// Get file icon based on file type
const getFileIcon = fileType => {
  if (!fileType)
    return <FileOutlined style={{ fontSize: 24, color: "#8c8c8c" }} />

  if (fileType.includes("pdf")) {
    return <FilePdfOutlined style={{ fontSize: 24, color: "#ff4d4f" }} />
  }
  if (fileType.includes("image")) {
    return <FileImageOutlined style={{ fontSize: 24, color: "#52c41a" }} />
  }
  if (fileType.includes("word") || fileType.includes("document")) {
    return <FileWordOutlined style={{ fontSize: 24, color: "#1890ff" }} />
  }
  if (fileType.includes("excel") || fileType.includes("spreadsheet")) {
    return <FileExcelOutlined style={{ fontSize: 24, color: "#52c41a" }} />
  }
  if (
    fileType.includes("zip") ||
    fileType.includes("rar") ||
    fileType.includes("compressed")
  ) {
    return <FileZipOutlined style={{ fontSize: 24, color: "#faad14" }} />
  }
  return <FileOutlined style={{ fontSize: 24, color: "#8c8c8c" }} />
}

// Get file type tag color
const getFileTypeColor = fileType => {
  if (!fileType) return "default"
  if (fileType.includes("pdf")) return "red"
  if (fileType.includes("image")) return "green"
  if (fileType.includes("word")) return "blue"
  if (fileType.includes("excel")) return "cyan"
  if (fileType.includes("zip")) return "orange"
  return "default"
}

// Secure Image Component
const SecureImage = ({ src, ...props }) => {
  const [imgSrc, setImgSrc] = useState("")
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let isMounted = true
    const fetchImage = async () => {
      try {
        const obj = JSON.parse(localStorage.getItem("authUser"))
        const token = obj && obj.accessToken ? obj.accessToken : ""

        const response = await fetch(src, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (response.ok) {
          const blob = await response.blob()
          const url = URL.createObjectURL(blob)
          if (isMounted) {
            setImgSrc(url)
            setLoading(false)
          }
        } else {
          setLoading(false)
        }
      } catch (error) {
        console.error("Error fetching image:", error)
        setLoading(false)
      }
    }

    if (src) {
      fetchImage()
    }

    return () => {
      isMounted = false
      if (imgSrc) URL.revokeObjectURL(imgSrc)
    }
  }, [src])

  if (loading) {
    return <Spin size="small" />
  }

  return <Image src={imgSrc} {...props} />
}

// Get physical file URL

const DocumentFiles = ({
  entityId,
  entityType,
  lstDocuments,
  loading,
  uploadLoading,
  error,
  success,
  onGetDocuments,
  onAddDocument,
  onDeleteDocument,
  onResetDocumentFlag,
  onClearDocuments,
  readOnly = false,
  showTitle = true,
  maxFiles = 10,
  acceptedFileTypes = "*",
  maxFileSize = 10, // in MB
  t,
}) => {
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewImage, setPreviewImage] = useState("")
  const [previewTitle, setPreviewTitle] = useState("")
  const [documentTypeEnum, setDocumentTypeEnum] = useState(
    documentTypeOptions[0]?.enum || 0
  )

  const selectOptions = documentTypeOptions.map(option => ({
    value: option.enum,
    label: option.label,
  }))

  // Fetch documents on mount
  useEffect(() => {
    if (entityId) {
      onGetDocuments(entityId, entityType)
    }
    return () => {
      onClearDocuments()
    }
  }, [entityId, entityType])

  // Handle success/error messages
  useEffect(() => {
    if (success) {
      message.success(success)
      setTimeout(() => onResetDocumentFlag(), 100)
    }
    if (error) {
      message.error(error)
      setTimeout(() => onResetDocumentFlag(), 100)
    }
  }, [success, error])

  // Handle file upload
  const handleUpload = useCallback(
    async file => {
      // Validate file size
      const fileSizeMB = file.size / 1024 / 1024
      if (fileSizeMB > maxFileSize) {
        message.error(`حجم فایل نباید بیشتر از ${maxFileSize} مگابایت باشد`)
        return false
      }

      // Check max files limit
      if (lstDocuments.length >= maxFiles) {
        message.error(`حداکثر ${maxFiles} فایل مجاز است`)
        return false
      }

      const formData = new FormData()
      formData.append("file", file)
      formData.append("entityId", entityId)
      formData.append("fileName", file.name)
      formData.append("fileType", file.type)
      formData.append("entityTypeEnum", entityType)
      formData.append("documentTypeEnum", String(documentTypeEnum))

      onAddDocument(formData)
      return false // Prevent default upload behavior
    },
    [
      entityId,
      entityType,
      documentTypeEnum,
      lstDocuments.length,
      maxFiles,
      maxFileSize,
      onAddDocument,
    ]
  )

  // Handle preview
  const handlePreview = async record => {
    const obj = JSON.parse(localStorage.getItem("authUser"))
    const token = obj && obj.accessToken ? obj.accessToken : ""

    if (record.fileType?.includes("image")) {
      try {
        const response = await fetch(getApiUrlNotificationFiles(record.s3Path), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        setPreviewImage(url)
        setPreviewTitle(record.fileName)
        setPreviewVisible(true)
      } catch (error) {
        message.error("خطا در دریافت تصویر")
      }
    } else {
      try {
        const response = await fetch(getApiUrlNotificationFiles(record.s3Path), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        window.open(url, "_blank")
      } catch (error) {
        message.error("خطا در دریافت فایل")
      }
    }
  }

  // Handle download
  const handleDownload = async record => {
    const obj = JSON.parse(localStorage.getItem("authUser"))
    const token = obj && obj.accessToken ? obj.accessToken : ""

    try {
      const response = await fetch(getApiUrlNotificationFiles(record.s3Path), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)

      const link = document.createElement("a")
      link.href = url
      link.download = record.fileName
      link.target = "_blank"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      message.error("خطا در دانلود فایل")
    }
  }

  // Table columns
  const columns = [
    {
      title: "فایل",
      dataIndex: "fileName",
      key: "fileName",
      render: (text, record) => (
        <Space>
          {record.fileType?.includes("image") ? (
            <SecureImage
              width={60}
              height={60}
              src={getApiUrlNotificationFiles(record.s3Path)}
              style={{ objectFit: "cover", borderRadius: 8 }}
              preview={{
                mask: <EyeOutlined />,
              }}
            />
          ) : (
            getFileIcon(record.fileType)
          )}
          <div>
            <Text strong ellipsis style={{ maxWidth: 200, display: "block" }}>
              {text}
            </Text>
            <Tag
              color={getFileTypeColor(record.fileType)}
              style={{ marginTop: 4 }}
            >
              {record.fileType?.split("/")[1]?.toUpperCase() || "FILE"}
            </Tag>
          </div>
        </Space>
      ),
    },
    {
      title: "نوع موجودیت",
      dataIndex: "entityTypeName",
      key: "entityTypeName",
      width: 120,
      render: text => <Tag color="purple">{text || "نامشخص"}</Tag>,
    },
    {
      title: "نوع فایل",
      dataIndex: "documentTypeName",
      key: "documentTypeName",
      width: 120,
      render: text => <Tag color="purple">{text || "نامشخص"}</Tag>,
    },
    {
      title: "عملیات",
      key: "actions",
      width: 150,
      align: "center",
      render: (_, record) => (
        <Space>
          <Tooltip title="مشاهده">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handlePreview(record)}
            />
          </Tooltip>
          <Tooltip title="دانلود">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          {!readOnly && (
            <Popconfirm
              title="آیا از حذف این فایل مطمئن هستید؟"
              onConfirm={() => onDeleteDocument(record.id)}
              okText="بله"
              cancelText="خیر"
            >
              <Tooltip title="حذف">
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  // Upload props
  const uploadProps = {
    name: "file",
    multiple: true,
    accept: acceptedFileTypes,
    showUploadList: false,
    beforeUpload: handleUpload,
    disabled: readOnly || uploadLoading || lstDocuments.length >= maxFiles,
  }

  return (
    <div>
      {showTitle && (
        <div>
          <Space>
            <PaperClipOutlined style={{ color: "#1890ff" }} />
            <Title level={5} style={{ margin: 0 }}>
              فایل‌های پیوست
            </Title>
            <Badge
              count={lstDocuments.length}
              style={{ backgroundColor: "#1890ff" }}
            />
          </Space>
        </div>
      )}

      {!readOnly && (
        <Card
          style={{ marginBottom: 16, borderRadius: 12 }}
          styles={{ body: { padding: 16 } }}
        >
          <Spin spinning={uploadLoading} tip="در حال آپلود...">
            <Row gutter={[12, 12]} style={{ marginBottom: 12 }} align="middle">
              <Col>
                <Text strong>نوع فایل</Text>
              </Col>
              <Col flex="auto">
                <Select
                  value={documentTypeEnum}
                  onChange={setDocumentTypeEnum}
                  options={selectOptions}
                  style={{ minWidth: 220 }}
                />
              </Col>
            </Row>
            <Dragger {...uploadProps} className="custom-dragger">
              <p className="ant-upload-drag-icon">
                <CloudUploadOutlined
                  style={{ fontSize: 48, color: "#1890ff" }}
                />
              </p>
              <p className="ant-upload-text">
                فایل را اینجا بکشید یا کلیک کنید
              </p>
              <p className="ant-upload-hint">
                حداکثر {maxFiles} فایل - هر فایل حداکثر {maxFileSize} مگابایت
              </p>
              {lstDocuments.length > 0 && (
                <Progress
                  percent={Math.round((lstDocuments.length / maxFiles) * 100)}
                  size="small"
                  style={{ maxWidth: 200, margin: "16px auto 0" }}
                  format={() => `${lstDocuments.length}/${maxFiles}`}
                />
              )}
            </Dragger>
          </Spin>
        </Card>
      )}

      <Card
        className="files-list-card"
        style={{ borderRadius: 12 }}
        styles={{ body: { padding: 0 } }}
      >
        <Spin spinning={loading}>
          <Table
            dataSource={lstDocuments}
            columns={columns}
            rowKey="id"
            pagination={
              lstDocuments.length > 5
                ? {
                    pageSize: 5,
                    showTotal: total => `${total} فایل`,
                    hideOnSinglePage: true,
                  }
                : false
            }
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="هیچ فایلی وجود ندارد"
                />
              ),
            }}
          />
        </Spin>
      </Card>

      {/* Preview Modal */}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={800}
        centered
      >
        <img
          alt={previewTitle}
          style={{ width: "100%", borderRadius: 8 }}
          src={previewImage}
        />
      </Modal>
    </div>
  )
}

const mapStateToProps = ({ Documents }) => ({
  lstDocuments: Documents.lstDocuments,
  loading: Documents.loading,
  uploadLoading: Documents.uploadLoading,
  error: Documents.error,
  success: Documents.success,
})

const mapDispatchToProps = {
  onGetDocuments: getDocuments,
  onAddDocument: addDocument,
  onDeleteDocument: deleteDocument,
  onResetDocumentFlag: resetDocumentFlag,
  onClearDocuments: clearDocuments,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(DocumentFiles))
